

























































































































import Vue from 'vue';
import Component, { mixins } from 'vue-class-component';
import HeaderFlyoutImageItem from '@/webcomponents/header/HeaderFlyoutImageItem.vue';
import TrackingPushService from '@/common/ts/tracking/TrackingPushService';
import ResizeUpdateMixin from '@/common/ts/resizing/ResizeUpdateMixin';
import { Watch } from 'vue-property-decorator';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import i18n from './i18n';
import { SearchEventBus, SearchEventBusEvents } from './SearchEventBus';

const SearchIcon = () => import('@/common/icons/search-new.svg');
const ArrowIcon = () => import('@/common/icons/arrow-right.svg');
const CloseIcon = () => import('@/common/icons/close-new.svg');
const CloseNewIcon = () => import('@/common/icons/close-new.svg');

const PER_PAGE_INITIAL = 8;
const PER_PAGE = 9;
const API_APPLICATION_NAME = 'brandhub';
const LOCAL_STORAGE_NAME = 'brandhubSearch';
const HEADER_JSON = 'application/json';
const HEADER_MARKET = 'COM';
const WAIT_FOR_SESSION_TO_BECOME_READY_TIME = 1000;
const DONE_TYPING_INTERVAL = 200;
const MAX_NUMBER_SUGGESTIONS = 10;

const SearchProps = Vue.extend({
  props: {
    apiUrl: {
      type: String,
      default: 'https://int.api.oneweb.mercedes-benz.com/fss-core/v1',
    },
    autoSuggestions: {
      type: Boolean,
      default: false,
    },
    searchSite: {
      type: String,
      default: 'all',
    },
  },
});

@Component({
  components: {
    HeaderFlyoutImageItem,
    SearchIcon,
    ArrowIcon,
    CloseIcon,
    CloseNewIcon,
  },
  i18n,
})
export default class Search extends mixins(SearchProps, ResizeUpdateMixin) {
  $refs!: {
    content: HTMLInputElement;
    input: HTMLInputElement;
    inputClose: HTMLButtonElement;
    searchCloseButton: HTMLButtonElement;
  };

  suggestions: string[] = [];
  results: string[] = [];
  sessionToken: string | null = null;
  sessionIsVerified = false;
  currentSearchTerm = '';
  searchText = '';
  currentOffset = 0;
  searchProfileIds = {
    'en-COM': {
      all: '69b80914-3006-4e3f-5aef-9f66e173522e',
      'amg-f1': '46a8ae05-9ba3-4bec-af60-946b30ae366c',
    },
    'de-COM': {
      all: '200b2e7e-de22-4db5-6f04-58b0f562ec61',
      'amg-f1': 'd21aacfb-429b-4d5f-ab26-9d022e452d95',
    },
  };

  fadeInSearch = false;
  isFocused = false;
  suggestionsVisible = false;
  storedOptions = {};
  typingTimer: number | undefined = undefined;
  activeSuggestionIndex = -1;
  noResults = false;
  loadMoreDisabled = false;
  fadeInForm = false;

  closeOverlay () {
    this.fadeInForm = false;
    this.suggestions = [];
    this.results = [];
    setTimeout(() => {
      this.fadeInSearch = false;
      this.$emit('search-close');
    }, 500);
  }

  onSubmit () {
    this.onSearchTerm(this.searchText);
  }

  handleDown () {
    if (this.isFocused && this.activeSuggestionIndex < this.suggestions.length) {
      this.activeSuggestionIndex += 1;
      this.searchText = this.suggestions[this.activeSuggestionIndex];
    }
  }

  handleUp () {
    if (this.isFocused && this.activeSuggestionIndex > -1) {
      this.activeSuggestionIndex -= 1;
      this.searchText = this.suggestions[this.activeSuggestionIndex];
    }
  }

  focusLogo (event: KeyboardEvent) {
    event.preventDefault();
    this.$emit('focus-logo');
  }

  focusInput () {
    this.$refs.input.focus();
  }

  focusCloseButton () {
    this.$refs.searchCloseButton.focus();
  }

  handleInputTab (event: KeyboardEvent) {
    if (this.isMobile || this.isTablet) {
      event.preventDefault();
      SearchEventBus.$emit(SearchEventBusEvents.FocusCloseButton);
    }
  }

  onFocus () {
    this.verifySession();
    this.isFocused = true;
    this.suggestionsVisible = true;
    if (this.searchText) {
      this.refreshSuggestions();
    }
  }

  onFocusOut () {
    setTimeout(() => {
      const { activeElement } = this.$el.getRootNode() as Document;
      if (activeElement && !activeElement.classList.contains('brandhub-search__suggestion-item')) {
        this.isFocused = false;
        this.suggestionsVisible = false;
      }
    }, 200);
  }

  onInputClose () {
    this.searchText = '';
    this.suggestionsVisible = false;
    TrackingPushService.getInstance().pushTrackingAttributes('link', 'Search', 'clear search');
  }

  @Watch('searchText')
  onSearchTextUpdate () {
    if (this.autoSuggestions && !this.suggestions.includes(this.searchText)) {
      clearTimeout(this.typingTimer);
      if (this.searchText) {
        this.typingTimer = setTimeout(this.refreshSuggestions, DONE_TYPING_INTERVAL);
      }
    }
  }

  onClickLoadMore () {
    if (!this.currentSearchTerm) {
      return;
    }

    this.loadMoreDisabled = true;
    this.currentOffset += PER_PAGE;
    this.refreshResults(this.currentSearchTerm, true);

    TrackingPushService.getInstance().pushTrackingAttributes('link', 'Search Results', 'load more');
  }

  onSuggestionClick (suggestion: string) {
    this.searchText = suggestion;
    this.onSearchTerm(suggestion);

    TrackingPushService.getInstance().pushTrackingAttributes('link', 'Search', suggestion);
  }

  onResultClick (url: string, title: string) {
    TrackingPushService.getInstance().pushTrackingAttributes('link', 'Search Results', title, url);
  }

  onSearchClick () {
    TrackingPushService.getInstance().pushTrackingAttributes('link', 'Search', 'search button');
  }

  onSearchTerm (searchTerm: string) {
    this.suggestions = [];
    this.noResults = false;
    this.suggestionsVisible = false;
    const scopeSearchTerm = searchTerm.trim();
    if (scopeSearchTerm !== this.currentSearchTerm) {
      this.currentSearchTerm = scopeSearchTerm;
      this.currentOffset = 0;

      if (searchTerm.length < 1) {
        this.results = [];
      } else {
        this.refreshResults(scopeSearchTerm, false);
      }
    }
  }

  resumeOrCreateSession () {
    if (this.getStoredOptionsForCurrentLocale().sessionToken) {
      this.sessionToken = this.getStoredOptionsForCurrentLocale().sessionToken;
    } else {
      this.createSession();
    }
  }

  refreshResults (searchTerm: string, loadMore: boolean) {
    // wait for session token and make sure it is validated
    if (!this.sessionToken || !this.sessionIsVerified) {
      this.verifySession();
      setTimeout(() => {
        this.refreshResults(searchTerm, loadMore);
      }, WAIT_FOR_SESSION_TO_BECOME_READY_TIME + 200);
    } else {
      const limit = loadMore ? PER_PAGE : PER_PAGE_INITIAL;
      const searchParams = new URLSearchParams({
        sp: this.searchProfileId.toString(),
        q: this.normalizeQuery(searchTerm).toString(),
        limit: limit.toString(),
        pageOffset: this.currentOffset.toString(),
      });
      fetch(`${this.apiUrl}/results?${searchParams}`, {
        method: 'GET',
        headers: {
          'Content-Type': HEADER_JSON,
          Accept: HEADER_JSON,
          SessionToken: this.sessionToken,
          ApplicationName: API_APPLICATION_NAME,
        },
      })
        .then((response) => response.json())
        .then((data) => {
          this.updateResults(data, searchTerm, loadMore);
        });
    }
  }

  updateResults (response: any, searchTerm: string, loadMore: boolean) {
    if (response.results.length) {
      if (loadMore) {
        this.results = this.results.concat(response.results);
      } else {
        this.results = response.results;
      }
      this.loadMoreDisabled = false;
    } else {
      this.noResults = true;
      this.results = [];
    }
  }

  verifySession () {
    if (!this.sessionToken) {
      this.createSession();
    } else if (!this.sessionIsVerified) {
      const searchParams = new URLSearchParams({
        tokenId: this.sessionToken,
      });
      fetch(`${this.apiUrl}/sessions/verify?${searchParams}`, {
        method: 'GET',
        headers: {
          ApplicationName: API_APPLICATION_NAME,
          Market: HEADER_MARKET,
          Language: this.language,
        },
      })
        .then(() => {
          this.sessionIsVerified = true;
        })
        .catch(() => {
          this.createSession();
        });
    }
  }

  createSession () {
    fetch(`${this.apiUrl}/sessions`, {
      method: 'POST',
      headers: {
        'Content-Type': HEADER_JSON,
        Accept: HEADER_JSON,
        ApplicationName: API_APPLICATION_NAME,
        Market: HEADER_MARKET,
        Language: this.language,
      },
    })
      .then((response) => response.json())
      .then((data) => {
        // backend will answer with "token is not usable yet (please wait 1000 milliseconds)" if token is used directly
        // after fetching it, so let's wait 1 second first!
        setTimeout(() => {
          if (data && data.token) {
            this.sessionToken = data.token;
            this.sessionIsVerified = true;
            this.getStoredOptionsForCurrentLocale().sessionToken = this.sessionToken;
            this.saveStoredOptions();
          }
        }, WAIT_FOR_SESSION_TO_BECOME_READY_TIME);
      });
  }

  getStoredOptions () {
    if (this.storedOptions) {
      return this.storedOptions;
    }
    if (window.localStorage) {
      const value = window.localStorage.getItem(LOCAL_STORAGE_NAME);
      if (value) {
        this.storedOptions = JSON.parse(value);
        return this.storedOptions;
      }
    }
    this.storedOptions = {};
    return this.storedOptions;
  }

  getStoredOptionsForCurrentLocale () {
    const storedOptions = this.getStoredOptions();
    const key = `${API_APPLICATION_NAME}_${HEADER_MARKET}_${this.language}`;
    if (!(key in storedOptions)) {
      storedOptions[key] = {};
    }
    return storedOptions[key];
  }

  saveStoredOptions () {
    if (window.localStorage) {
      const value = this.storedOptions || {};
      window.localStorage.setItem(LOCAL_STORAGE_NAME, JSON.stringify(value));
    }
  }

  refreshSuggestions () {
    // wait for session token and make sure it is validated
    if (!this.sessionToken || !this.sessionIsVerified) {
      this.verifySession();
      setTimeout(() => {
        this.refreshSuggestions();
      }, WAIT_FOR_SESSION_TO_BECOME_READY_TIME + 200);
    } else {
      const searchParams = new URLSearchParams({
        searchProfile: this.searchProfileId,
        query: this.normalizeQuery(this.searchText),
      });
      fetch(`${this.apiUrl}/suggestions?${searchParams}`, {
        method: 'GET',
        headers: {
          'Content-Type': HEADER_JSON,
          Accept: HEADER_JSON,
        },
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.suggests > MAX_NUMBER_SUGGESTIONS) {
            this.suggestions = data.suggests.slice(0, MAX_NUMBER_SUGGESTIONS + 1);
          } else {
            this.suggestions = data.suggests;
          }
          this.suggestionsVisible = true;
        });
    }
  }

  suggestionPrefix (suggestion: string) {
    const index = suggestion.toLowerCase().indexOf(this.searchText.toLowerCase());
    return suggestion.substring(0, index);
  }

  suggestionMiddle (suggestion: string) {
    const index = suggestion.toLowerCase().indexOf(this.searchText.toLowerCase());
    return suggestion.substring(index, this.searchText.length);
  }

  suggestionSuffix (suggestion: string) {
    return suggestion.substring(this.searchText.length, suggestion.length);
  }

  suggestionMiddleClass (suggestion: string) {
    const index = suggestion.toLowerCase().indexOf(this.searchText.toLowerCase());
    return {
      'brandhub-search__suggestion-middle--no-match': index === -1,
    };
  }

  normalizeQuery (q: string) {
    let scopedQuery = q;
    if (scopedQuery) {
      scopedQuery = scopedQuery.replace(/'/g, ' ');
      scopedQuery = scopedQuery.replace(/"/g, ' ');
    }
    return scopedQuery;
  }

  filterHtml (html: string) {
    const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
    return parsedHtml.body.textContent || '';
  }

  disableScroll () {
    disableBodyScroll(this.$refs.content);
  }

  enableScroll () {
    enableBodyScroll(this.$refs.content);
  }

  get rootClass () {
    return {
      'brandhub-search--in': this.fadeInSearch,
      'brandhub-search--iOS': this.isIOS,
    };
  }

  get rootStyle () {
    const listPadding = 70;
    const itemHeight = 22;
    const itemSpacing = 24;
    return {
      '--suggestions-list-height': `${listPadding + (itemHeight + itemSpacing) * this.suggestions.length}px`,
    };
  }

  get formClass () {
    return {
      'brandhub-search__form--in': this.fadeInForm,
      'brandhub-search__form--focused': this.isFocused,
    };
  }

  get suggestionsClass () {
    return {
      'brandhub-search__suggestions-list--visible': this.suggestionsVisible,
    };
  }

  get language () {
    const language = document.querySelector('html')?.getAttribute('lang');
    if (language) {
      return language === 'de-DE' || language === 'de' ? 'de' : 'en';
    }
    return 'de';
  }

  get searchProfileId () {
    return this.searchProfileIds[`${this.language}-${HEADER_MARKET}`][this.searchSite];
  }

  get topResults () {
    return this.results.slice(0, 2);
  }

  get bottomResults () {
    return this.results.slice(2);
  }

  get isIOS () {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  }

  suggestionItemClass (index: number) {
    return {
      'brandhub-search__suggestion-item--active': this.activeSuggestionIndex === index,
    };
  }

  mounted (): void {
    SearchEventBus.$on(SearchEventBusEvents.FocusInput, this.focusInput);
    SearchEventBus.$on(SearchEventBusEvents.FocusCloseButton, this.focusCloseButton);
    this.resumeOrCreateSession();

    setTimeout(() => {
      this.fadeInSearch = true;
    }, 20);

    setTimeout(() => {
      this.fadeInForm = true;
    }, 500);

    setTimeout(() => {
      this.$refs.input.focus();
    }, 1000);

    this.disableScroll();
  }

  beforeDestroy () {
    this.enableScroll();
  }
}
